import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { contactAPI, SITE_NAMES, SITE_COLORS } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { SEO } from '../seo/SEO';

export const ContactPage = ({ siteKey }) => {
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    site: siteKey
  });

  const siteName = SITE_NAMES[siteKey];
  const colors = SITE_COLORS[siteKey];

  useEffect(() => {
    const loadContactInfo = async () => {
      try {
        const response = await contactAPI.getInfo(siteKey);
        setContactInfo(response.data);
      } catch (error) {
        console.error('Error loading contact info:', error);
      } finally {
        setLoading(false);
      }
    };
    loadContactInfo();
  }, [siteKey]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await contactAPI.sendMessage({ ...formData, site: siteKey });
      setSubmitted(true);
      toast.success('Zpráva byla úspesne odeslána');
    } catch (error) {
      toast.error('Nepodarilo se odeslat zprávu');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="py-12">
      <SEO
        siteKey={siteKey}
        title="Kontakt"
        description={`Kontaktní údaje na ${SITE_NAMES[siteKey]} v Nové Roli. Telefon, e-mail, adresa a otevírací doba. Neváhejte nás kontaktovat s jakýmkoli dotazem.`}
        keywords={['kontakt', 'telefon', 'adresa', 'email', 'otevírací doba']}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10">
          <Link to={`/${siteKey}`} className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#45AAF2] mb-4" data-testid="back-link">
            <ArrowLeft size={18} />
            Zpet na uvodní stránku
          </Link>
          <h1 className="text-4xl font-bold text-[#2D3748]" data-testid="page-title">Kontakt</h1>
          <p className="text-[#64748B] mt-2">Neváhejte nás kontaktovat s jakýmkoli dotazem</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <div className="bg-white rounded-3xl border-3 border-[#F0F4F8] p-8 mb-8">
              <h2 className="text-2xl font-bold text-[#2D3748] mb-6">Kontaktní údaje</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${colors.primary}15` }}>
                    <MapPin style={{ color: colors.primary }} size={24} />
                  </div>
                  <div>
                    <div className="font-medium text-[#2D3748] mb-1">Adresa</div>
                    <div className="text-[#64748B]">{contactInfo?.address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#FECA57]/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#FF9F43]" size={24} />
                  </div>
                  <div>
                    <div className="font-medium text-[#2D3748] mb-1">Telefon</div>
                    <a href={`tel:${contactInfo?.phone}`} style={{ color: colors.primary }} className="hover:underline">
                      {contactInfo?.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${colors.secondary}15` }}>
                    <Mail style={{ color: colors.secondary }} size={24} />
                  </div>
                  <div>
                    <div className="font-medium text-[#2D3748] mb-1">E-mail</div>
                    <a href={`mailto:${contactInfo?.email}`} style={{ color: colors.primary }} className="hover:underline">
                      {contactInfo?.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#FF6B6B]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="text-[#FF6B6B]" size={24} />
                  </div>
                  <div>
                    <div className="font-medium text-[#2D3748] mb-1">Provozní doba</div>
                    <div className="text-[#64748B]">{contactInfo?.opening_hours}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl h-64 flex items-center justify-center" style={{ backgroundColor: `${colors.primary}10` }}>
              <div className="text-center">
                <MapPin size={48} style={{ color: colors.primary }} className="mx-auto mb-2" />
                <div className="font-medium text-[#2D3748]">{siteName}</div>
                <div className="text-sm text-[#64748B]">{contactInfo?.address}</div>
              </div>
            </div>
          </div>

          <div>
            <div className="contact-form">
              <h2 className="text-2xl font-bold text-[#2D3748] mb-6">Napište nám</h2>
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${colors.secondary}20` }}>
                    <CheckCircle size={32} style={{ color: colors.secondary }} />
                  </div>
                  <h3 className="text-xl font-semibold text-[#2D3748] mb-2">Zpráva odeslána!</h3>
                  <p className="text-[#64748B] mb-6">Dekujeme za váš dotaz. Ozveme se vám co nejdríve.</p>
                  <Button 
                    variant="outline" 
                    className="rounded-full"
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: '', email: '', phone: '', subject: '', message: '', site: siteKey });
                    }}
                    data-testid="send-another-btn"
                  >
                    Odeslat další zprávu
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Jméno a príjmení *</Label>
                      <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="rounded-xl" data-testid="contact-name" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail *</Label>
                      <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="rounded-xl" data-testid="contact-email" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon</Label>
                      <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className="rounded-xl" data-testid="contact-phone" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">Predmet *</Label>
                      <Input id="subject" name="subject" value={formData.subject} onChange={handleChange} required className="rounded-xl" data-testid="contact-subject" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Zpráva *</Label>
                    <Textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="rounded-xl resize-none" data-testid="contact-message" />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="w-full rounded-full h-12 text-white"
                    style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})` }}
                    data-testid="contact-submit"
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Odesílání...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2"><Send size={18} />Odeslat zprávu</span>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
